import { useState, useEffect, useRef } from "react";
import { supabase, uploadPhotoToStorage } from "@/lib/supabase";
import MemberPhotoPlaceholder from "@/components/base/MemberPhotoPlaceholder";
import PhotoPositionSelector from "@/components/base/PhotoPositionSelector";
import { normalizePhotoPosition, PHOTO_POSITION_CLASS } from "@/lib/avatar";
import PhoneInput from "@/components/base/PhoneInput";
import { formatPhone } from "@/lib/phone";

interface MemberProfile {
  id: string;
  full_name: string;
  city_state: string;
  profession: string;
  description: string;
  whatsapp: string;
  country_code: string;
  email: string;
  instagram: string;
  website: string;
  photo: string | null;
  photo_position: string;
  status: string;
  approval_token: string | null;
  bio_long: string;
  skills: string;
  languages: string;
  education: string;
  services: string;
  linkedin: string;
  facebook: string;
  tiktok: string;
  experience_years: string;
  has_physical_business: boolean;
  business_name: string;
  business_address: string;
  business_city: string;
  business_state: string;
  business_zip: string;
}

function normalizeUrl(raw: string): string {
  const trimmed = raw.trim();
  if (!trimmed) return "";
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
}

function normalizeMember(m: MemberProfile): MemberProfile {
  return {
    ...m,
    full_name: m.full_name || '',
    city_state: m.city_state || '',
    profession: m.profession || '',
    description: m.description || '',
    whatsapp: formatPhone(m.whatsapp || '', m.country_code || '+1'),
    country_code: m.country_code || '+1',
    email: m.email || '',
    instagram: m.instagram || '',
    website: m.website || '',
    bio_long: m.bio_long || '',
    skills: m.skills || '',
    languages: m.languages || '',
    education: m.education || '',
    services: m.services || '',
    linkedin: m.linkedin || '',
    facebook: m.facebook || '',
    tiktok: m.tiktok || '',
    experience_years: m.experience_years || '',
    photo_position: m.photo_position || 'top',
    has_physical_business: !!m.has_physical_business,
    business_name: m.business_name || '',
    business_address: m.business_address || '',
    business_city: m.business_city || '',
    business_state: m.business_state || '',
    business_zip: m.business_zip || '',
  };
}

// Comprime a imagem no cliente: máx 800x800px, JPEG ~80% (mesmo mecanismo do JoinForm)
function compressImage(file: File): Promise<{ blob: Blob; preview: string }> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const MAX_DIM = 800;
      const QUALITY = 0.8;
      let { naturalWidth: w, naturalHeight: h } = img;
      if (w > MAX_DIM || h > MAX_DIM) {
        const ratio = Math.min(MAX_DIM / w, MAX_DIM / h);
        w = Math.round(w * ratio);
        h = Math.round(h * ratio);
      }

      const canvas = document.createElement("canvas");
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext("2d");
      if (!ctx) {
        reject(new Error("Falha ao processar a imagem no navegador."));
        return;
      }
      ctx.drawImage(img, 0, 0, w, h);

      const preview = canvas.toDataURL("image/jpeg", QUALITY);
      canvas.toBlob(
        (blob) => {
          if (!blob) {
            reject(new Error("Falha ao gerar a imagem comprimida."));
            return;
          }
          resolve({ blob, preview });
        },
        "image/jpeg",
        QUALITY,
      );
    };
    img.onerror = () => reject(new Error("Erro ao processar a imagem. Tente outra foto."));
    img.src = URL.createObjectURL(file);
  });
}

interface Props {
  member: MemberProfile;
  onClose: () => void;
  onSaved: () => void;
  onToast: (type: "success" | "error", message: string) => void;
}

export default function MemberEditModal({ member, onClose, onSaved, onToast }: Props) {
  const [formData, setFormData] = useState<MemberProfile>(normalizeMember(member));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [photoStatus, setPhotoStatus] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);
  const photoBlobRef = useRef<Blob | null>(null);
  const shouldRemovePhotoRef = useRef(false);

  useEffect(() => {
    setFormData(normalizeMember(member));
    setError("");
    setPhotoPreview(null);
    setPhotoStatus("");
    photoBlobRef.current = null;
    shouldRemovePhotoRef.current = false;
  }, [member]);

  useEffect(() => {
    function handleEsc(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, [onClose]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>,
  ) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handlePhotoSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      onToast("error", "Selecione um arquivo de imagem (JPEG, PNG ou WebP).");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      onToast("error", "A imagem deve ter no máximo 5MB.");
      return;
    }

    setPhotoStatus("Comprimindo imagem...");

    try {
      const { blob, preview } = await compressImage(file);
      setPhotoPreview(preview);
      photoBlobRef.current = blob;

      const sizeKb = Math.round(blob.size / 1024);
      setPhotoStatus(`Pronta! ${sizeKb}KB — será enviada ao salvar.`);
    } catch (err) {
      setPhotoStatus("");
      setPhotoPreview(null);
      photoBlobRef.current = null;
      onToast("error", (err as Error)?.message || "Erro ao processar a imagem.");
    }
  };

  const handleRemovePhoto = () => {
    setPhotoPreview(null);
    setPhotoStatus("");
    photoBlobRef.current = null;
    shouldRemovePhotoRef.current = true;
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.full_name.trim() || !formData.email.trim()) {
      setError("Nome e email são obrigatórios.");
      return;
    }

    setSaving(true);
    setError("");

    let newPhotoUrl: string | null = formData.photo;

    // Se选择了 remover, foto vai pra null
    if (shouldRemovePhotoRef.current) {
      newPhotoUrl = null;
    }

    // Upload da foto direto pro Supabase Storage (mesmo mecanismo do JoinForm)
    if (photoBlobRef.current) {
      setPhotoStatus("Enviando foto para o servidor...");
      try {
        const originalName = (fileInputRef.current?.files?.[0]?.name) || "foto.jpg";
        newPhotoUrl = await uploadPhotoToStorage(photoBlobRef.current, originalName);
        setPhotoStatus("");
      } catch (uploadErr: any) {
        setError(uploadErr?.message || "Erro inesperado ao enviar a foto.");
        setSaving(false);
        setPhotoStatus("");
        return;
      }
    }

    const { error: updateError } = await supabase
      .from("members")
      .update({
        full_name: formData.full_name.trim(),
        profession: formData.profession.trim(),
        city_state: formData.city_state.trim(),
        description: formData.description.trim(),
        whatsapp: formData.whatsapp.replace(/\D/g, ""),
        country_code: formData.country_code,
        email: formData.email.trim(),
        instagram: formData.instagram.trim(),
        website: normalizeUrl(formData.website),
        bio_long: formData.bio_long.trim(),
        skills: formData.skills.trim(),
        languages: formData.languages.trim(),
        education: formData.education.trim(),
        services: formData.services.trim(),
        linkedin: normalizeUrl(formData.linkedin),
        facebook: normalizeUrl(formData.facebook),
        tiktok: formData.tiktok.trim(),
        experience_years: formData.experience_years.trim(),
        photo_position: formData.photo_position || 'top',
        has_physical_business: formData.has_physical_business,
        business_name: formData.has_physical_business ? formData.business_name.trim() || null : null,
        business_address: formData.has_physical_business ? formData.business_address.trim() || null : null,
        business_city: formData.has_physical_business ? formData.business_city.trim() || null : null,
        business_state: formData.has_physical_business ? formData.business_state.trim() || null : null,
        business_zip: formData.has_physical_business ? formData.business_zip.trim() || null : null,
        status: formData.status,
        photo: newPhotoUrl,
      })
      .eq("id", member.id);

    if (updateError) {
      setError(updateError.message || "Erro ao salvar. Tente novamente.");
      setSaving(false);
      return;
    }

    onToast("success", `Perfil de ${formData.full_name} atualizado com sucesso!`);
    onSaved();
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) onClose();
  };

  const statusOptions = [
    { value: "pending", label: "Pendente" },
    { value: "approved", label: "Aprovada" },
    { value: "active", label: "Ativa" },
    { value: "rejected", label: "Rejeitada" },
  ];

  const currentPhotoUrl = shouldRemovePhotoRef.current ? null : (photoPreview || formData.photo);
  const hasExistingPhoto = !!formData.photo && !shouldRemovePhotoRef.current;

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto py-10"
      onClick={handleBackdropClick}
    >
      {/* Backdrop */}
      <div className="fixed inset-0 bg-brown/40 backdrop-blur-sm" />

      {/* Modal */}
      <div className="relative z-10 w-full max-w-2xl mx-4 bg-white rounded-2xl shadow-lg animate-fade-in-up">
        {/* Header */}
        <div className="flex items-center justify-between px-6 md:px-8 py-5 border-b border-nude/50">
          <div className="flex items-center gap-3">
            <span className="w-10 h-10 flex items-center justify-center rounded-full bg-primary/10">
              <i className="ri-edit-line text-primary text-lg" />
            </span>
            <div>
              <h3 className="font-display text-lg text-brown">Editar Perfil</h3>
              <p className="font-body text-xs text-brown/50">{member.full_name}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-9 h-9 flex items-center justify-center rounded-full text-brown/40 hover:text-brown hover:bg-beige transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="px-6 md:px-8 py-6 max-h-[70vh] overflow-y-auto space-y-5">
          {/* Foto do Perfil */}
          <div>
            <label className="font-body text-sm text-brown block mb-3">
              Foto do perfil
            </label>
            <div className="flex items-start gap-4">
              {/* Current photo / preview / placeholder */}
              <div className="w-24 h-24 rounded-full overflow-hidden shrink-0">
                {currentPhotoUrl ? (
                  <img
                    src={currentPhotoUrl}
                    alt={formData.full_name}
                    className={`w-full h-full object-cover ${PHOTO_POSITION_CLASS[normalizePhotoPosition(formData.photo_position)]}`}
                  />
                ) : (
                  <MemberPhotoPlaceholder size="lg" rounded="full" />
                )}
              </div>

              <div className="flex flex-col gap-2">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  onChange={handlePhotoSelect}
                  className="hidden"
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={saving}
                  className="px-4 py-2 rounded-lg bg-background-100 border border-nude text-brown font-body text-sm hover:bg-beige transition-colors disabled:opacity-50 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-upload-line mr-1.5" />
                  {currentPhotoUrl ? "Trocar foto" : "Adicionar foto"}
                </button>

                {(photoPreview || hasExistingPhoto) && (
                  <button
                    type="button"
                    onClick={handleRemovePhoto}
                    disabled={saving}
                    className="px-4 py-2 rounded-lg bg-red-50 border border-red-200 text-red-600 font-body text-sm hover:bg-red-100 transition-colors disabled:opacity-50 whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-delete-bin-line mr-1.5" />
                    {photoPreview ? "Cancelar" : "Remover foto"}
                  </button>
                )}

                {photoStatus && (
                  <p className="font-body text-xs text-primary flex items-center gap-1.5">
                    <i className="ri-loader-4-line animate-spin" />
                    {photoStatus}
                  </p>
                )}

                <p className="font-body text-[11px] text-brown/40 mt-0.5">
                  JPEG, PNG ou WebP. Máx. 2MB.
                </p>

                <div className="mt-1">
                  <span className="font-body text-xs text-brown/60 block mb-1.5">Enquadramento da foto</span>
                  <PhotoPositionSelector
                    value={formData.photo_position}
                    onChange={(v) => setFormData({ ...formData, photo_position: v })}
                    disabled={saving}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Status */}
          <div>
            <label className="font-body text-sm text-brown block mb-2">
              Status
            </label>
            <select
              name="status"
              value={formData.status}
              onChange={handleChange}
              className="w-full px-4 py-3 rounded-lg border border-nude bg-beige/50 font-body text-sm text-brown focus:outline-none focus:border-primary transition-colors appearance-none"
            >
              {statusOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>

          {/* Nome */}
          <div>
            <label className="font-body text-sm text-brown block mb-2">
              Nome completo <span className="text-red-400">*</span>
            </label>
            <input
              type="text" name="full_name" required
              value={formData.full_name} onChange={handleChange}
              className="w-full px-4 py-3 rounded-lg border border-nude bg-beige/50 font-body text-sm text-brown placeholder-brown/40 focus:outline-none focus:border-primary transition-colors"
            />
          </div>

          {/* Profissão + Cidade */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
              <label className="font-body text-sm text-brown block mb-2">Profissão</label>
              <input
                type="text" name="profession"
                value={formData.profession} onChange={handleChange}
                className="w-full px-4 py-3 rounded-lg border border-nude bg-beige/50 font-body text-sm text-brown placeholder-brown/40 focus:outline-none focus:border-primary transition-colors"
              />
            </div>
            <div>
              <label className="font-body text-sm text-brown block mb-2">Cidade / Estado</label>
              <input
                type="text" name="city_state"
                value={formData.city_state} onChange={handleChange}
                className="w-full px-4 py-3 rounded-lg border border-nude bg-beige/50 font-body text-sm text-brown placeholder-brown/40 focus:outline-none focus:border-primary transition-colors"
              />
            </div>
          </div>

          {/* Descrição */}
          <div>
            <label className="font-body text-sm text-brown block mb-2">Descrição curta</label>
            <textarea
              name="description" maxLength={300}
              value={formData.description} onChange={handleChange}
              rows={2}
              className="w-full px-4 py-3 rounded-lg border border-nude bg-beige/50 font-body text-sm text-brown placeholder-brown/40 focus:outline-none focus:border-primary transition-colors resize-none"
            />
            <p className="font-body text-xs text-brown/40 mt-1 text-right">{formData.description.length}/300</p>
          </div>

          {/* Email + WhatsApp */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
              <label className="font-body text-sm text-brown block mb-2">
                Email <span className="text-red-400">*</span>
              </label>
              <input
                type="email" name="email" required
                value={formData.email} onChange={handleChange}
                className="w-full px-4 py-3 rounded-lg border border-nude bg-beige/50 font-body text-sm text-brown placeholder-brown/40 focus:outline-none focus:border-primary transition-colors"
              />
            </div>
            <div>
              <label className="font-body text-sm text-brown block mb-2">WhatsApp</label>
              <PhoneInput
                countryCode={formData.country_code}
                whatsapp={formData.whatsapp}
                onChange={({ countryCode, whatsapp }) =>
                  setFormData({ ...formData, country_code: countryCode, whatsapp })
                }
                disabled={saving}
              />
            </div>
          </div>

          <div className="border-t border-nude pt-3" />

          {/* Bio longa */}
          <div>
            <label className="font-body text-sm text-brown block mb-2">Bio completa</label>
            <textarea
              name="bio_long" maxLength={1000}
              value={formData.bio_long} onChange={handleChange}
              rows={4}
              className="w-full px-4 py-3 rounded-lg border border-nude bg-beige/50 font-body text-sm text-brown placeholder-brown/40 focus:outline-none focus:border-primary transition-colors resize-none"
            />
            <p className="font-body text-xs text-brown/40 mt-1 text-right">{formData.bio_long.length}/1000</p>
          </div>

          {/* Habilidades + Idiomas */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
              <label className="font-body text-sm text-brown block mb-2">Habilidades</label>
              <input
                type="text" name="skills"
                value={formData.skills} onChange={handleChange}
                className="w-full px-4 py-3 rounded-lg border border-nude bg-beige/50 font-body text-sm text-brown placeholder-brown/40 focus:outline-none focus:border-primary transition-colors"
              />
            </div>
            <div>
              <label className="font-body text-sm text-brown block mb-2">Idiomas</label>
              <input
                type="text" name="languages"
                value={formData.languages} onChange={handleChange}
                className="w-full px-4 py-3 rounded-lg border border-nude bg-beige/50 font-body text-sm text-brown placeholder-brown/40 focus:outline-none focus:border-primary transition-colors"
              />
            </div>
          </div>

          {/* Formação + Experiência */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
              <label className="font-body text-sm text-brown block mb-2">Formação</label>
              <input
                type="text" name="education"
                value={formData.education} onChange={handleChange}
                className="w-full px-4 py-3 rounded-lg border border-nude bg-beige/50 font-body text-sm text-brown placeholder-brown/40 focus:outline-none focus:border-primary transition-colors"
              />
            </div>
            <div>
              <label className="font-body text-sm text-brown block mb-2">Anos de experiência</label>
              <input
                type="text" name="experience_years"
                value={formData.experience_years} onChange={handleChange}
                className="w-full px-4 py-3 rounded-lg border border-nude bg-beige/50 font-body text-sm text-brown placeholder-brown/40 focus:outline-none focus:border-primary transition-colors"
              />
            </div>
          </div>

          {/* Serviços */}
          <div>
            <label className="font-body text-sm text-brown block mb-2">Serviços</label>
            <textarea
              name="services" maxLength={500}
              value={formData.services} onChange={handleChange}
              rows={2}
              className="w-full px-4 py-3 rounded-lg border border-nude bg-beige/50 font-body text-sm text-brown placeholder-brown/40 focus:outline-none focus:border-primary transition-colors resize-none"
            />
            <p className="font-body text-xs text-brown/40 mt-1 text-right">{formData.services.length}/500</p>
          </div>

          {/* Redes sociais */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
              <label className="font-body text-sm text-brown block mb-2">Instagram</label>
              <input
                type="text" name="instagram"
                value={formData.instagram} onChange={handleChange}
                className="w-full px-4 py-3 rounded-lg border border-nude bg-beige/50 font-body text-sm text-brown placeholder-brown/40 focus:outline-none focus:border-primary transition-colors"
              />
            </div>
            <div>
              <label className="font-body text-sm text-brown block mb-2">LinkedIn</label>
              <input
                type="text" name="linkedin"
                value={formData.linkedin} onChange={handleChange}
                className="w-full px-4 py-3 rounded-lg border border-nude bg-beige/50 font-body text-sm text-brown placeholder-brown/40 focus:outline-none focus:border-primary transition-colors"
                placeholder="linkedin.com/in/seuperfil"
              />
            </div>
            <div>
              <label className="font-body text-sm text-brown block mb-2">Facebook</label>
              <input
                type="text" name="facebook"
                value={formData.facebook} onChange={handleChange}
                className="w-full px-4 py-3 rounded-lg border border-nude bg-beige/50 font-body text-sm text-brown placeholder-brown/40 focus:outline-none focus:border-primary transition-colors"
                placeholder="facebook.com/seuperfil"
              />
            </div>
            <div>
              <label className="font-body text-sm text-brown block mb-2">TikTok</label>
              <input
                type="text" name="tiktok"
                value={formData.tiktok} onChange={handleChange}
                className="w-full px-4 py-3 rounded-lg border border-nude bg-beige/50 font-body text-sm text-brown placeholder-brown/40 focus:outline-none focus:border-primary transition-colors"
              />
            </div>
          </div>

          <div>
            <label className="font-body text-sm text-brown block mb-2">Website / Portfólio</label>
            <input
              type="text" name="website"
              value={formData.website} onChange={handleChange}
              className="w-full px-4 py-3 rounded-lg border border-nude bg-beige/50 font-body text-sm text-brown placeholder-brown/40 focus:outline-none focus:border-primary transition-colors"
              placeholder="seusite.com"
            />
          </div>

          <div className="border-t border-nude pt-3" />

          {/* Negócio com endereço físico */}
          <div>
            <label className="flex items-start gap-3 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={formData.has_physical_business}
                onChange={(e) => setFormData({ ...formData, has_physical_business: e.target.checked })}
                className="mt-0.5 w-4 h-4 accent-primary cursor-pointer"
              />
              <span className="font-body text-sm text-brown">
                Tenho um negócio com endereço físico
              </span>
            </label>

            {formData.has_physical_business && (
              <div className="mt-4 space-y-4">
                <div>
                  <label className="font-body text-sm text-brown block mb-2">Nome do negócio</label>
                  <input
                    type="text" name="business_name"
                    value={formData.business_name} onChange={handleChange}
                    className="w-full px-4 py-3 rounded-lg border border-nude bg-beige/50 font-body text-sm text-brown placeholder-brown/40 focus:outline-none focus:border-primary transition-colors"
                    placeholder="Nome do negócio"
                  />
                </div>
                <div>
                  <label className="font-body text-sm text-brown block mb-2">Endereço</label>
                  <input
                    type="text" name="business_address"
                    value={formData.business_address} onChange={handleChange}
                    className="w-full px-4 py-3 rounded-lg border border-nude bg-beige/50 font-body text-sm text-brown placeholder-brown/40 focus:outline-none focus:border-primary transition-colors"
                    placeholder="Rua, número, complemento"
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                  <div>
                    <label className="font-body text-sm text-brown block mb-2">Cidade</label>
                    <input
                      type="text" name="business_city"
                      value={formData.business_city} onChange={handleChange}
                      className="w-full px-4 py-3 rounded-lg border border-nude bg-beige/50 font-body text-sm text-brown placeholder-brown/40 focus:outline-none focus:border-primary transition-colors"
                      placeholder="Cidade"
                    />
                  </div>
                  <div>
                    <label className="font-body text-sm text-brown block mb-2">Estado</label>
                    <input
                      type="text" name="business_state"
                      value={formData.business_state} onChange={handleChange}
                      className="w-full px-4 py-3 rounded-lg border border-nude bg-beige/50 font-body text-sm text-brown placeholder-brown/40 focus:outline-none focus:border-primary transition-colors"
                      placeholder="FL"
                    />
                  </div>
                  <div>
                    <label className="font-body text-sm text-brown block mb-2">ZIP</label>
                    <input
                      type="text" name="business_zip"
                      value={formData.business_zip} onChange={handleChange}
                      className="w-full px-4 py-3 rounded-lg border border-nude bg-beige/50 font-body text-sm text-brown placeholder-brown/40 focus:outline-none focus:border-primary transition-colors"
                      placeholder="CEP / ZIP"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          {error && (
            <div className="px-4 py-3 rounded-lg bg-red-50 border border-red-200 text-red-700 font-body text-sm">
              {error}
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3 pt-3 border-t border-nude">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-6 py-3 rounded-full border border-nude text-brown font-body text-sm tracking-wide uppercase hover:bg-beige transition-colors whitespace-nowrap cursor-pointer"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex-1 px-6 py-3 rounded-full bg-primary text-white font-body text-sm tracking-wide uppercase hover:bg-primary-dark transition-colors disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
            >
              {saving ? (
                <span className="flex items-center justify-center gap-2">
                  <i className="ri-loader-4-line animate-spin" />
                  {photoStatus ? "Enviando foto..." : "Salvando..."}
                </span>
              ) : (
                <span className="flex items-center justify-center gap-2">
                  <i className="ri-save-line" />
                  Salvar alterações
                </span>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}